import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useAuth } from '../hooks/useAuth';
import { partnershipAPI, networkAPI } from '../services/api';
import { Card, Button, Badge, SectionHeader, Table, Td, Input, Select, Modal, StatCard, Empty, Spinner } from '../components/ui';
import toast from 'react-hot-toast';
import * as mapUtil from '../utils/partnershipMap';

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

const SITE_STATUS_COLOR = {
  planned: 'text-muted', funded: 'blue', equipment_assigned: 'amber',
  installed: 'cyan', live: 'green', operating: 'green',
};
const SITE_STATUS_LABEL = {
  planned: 'Planned', funded: 'Funded', equipment_assigned: 'Equipment Assigned',
  installed: 'Installed', live: 'Live', operating: 'Operating',
};
const SITE_STATUSES = ['planned', 'funded', 'equipment_assigned', 'installed', 'live', 'operating'];

const EQUIPMENT_STATUS_COLOR = {
  in_stock: 'text-muted', assigned_to_site: 'amber', deployed: 'green',
  faulty: 'red', repaired: 'blue', retired: 'text-muted',
};
const EQUIPMENT_STATUS_LABEL = {
  in_stock: 'In Stock', assigned_to_site: 'Assigned to Site', deployed: 'Deployed',
  faulty: 'Faulty', repaired: 'Repaired', retired: 'Retired',
};
const EQUIPMENT_STATUSES = ['in_stock', 'assigned_to_site', 'deployed', 'faulty', 'repaired', 'retired'];

const UPDATE_TYPES = ['milestone', 'installation', 'performance', 'general'];
const UPDATE_TYPE_COLOR = { milestone: 'green', installation: 'blue', performance: 'purple', general: 'text-muted' };
const UPDATE_TYPE_LABEL = { milestone: 'Milestone', installation: 'Installation', performance: 'Performance', general: 'General' };

// Capacity management — color bands for the utilization badge.
function utilizationColor(pct) {
  if (pct >= 80) return 'red';
  if (pct >= 60) return 'amber';
  return 'green';
}
const EMPTY_EQUIPMENT_ROW = { type: '', quantity: '1', unitPrice: '' };

// Tier 1 — MikroTik/RADIUS network hardware, read-only in the partner portal.
const MONITORING_COLOR = { pending: 'text-muted', online: 'green', offline: 'red' };
const MONITORING_LABEL = { pending: 'Pending', online: 'Online', offline: 'Offline' };

function fmtUGX(n) {
  return 'UGX ' + Math.round(n || 0).toLocaleString();
}

// A PartnerEarning's period as "YYYY-MM" — the net-model records carry it
// directly; gross-model records only have periodStart/periodEnd.
function fmtEarningPeriod(e) {
  if (e.period) return e.period;
  if (e.periodStart) return new Date(e.periodStart).toISOString().slice(0, 7);
  return '—';
}

function errMsg(err, fallback) {
  return err?.message || fallback || 'Something went wrong';
}

// Site cost tracking + net-earnings ("Model B")
const SITE_COST_CATEGORIES = ['internet_subscription', 'licensing', 'maintenance', 'device_replacement', 'upgrade', 'custom'];
const SITE_COST_CATEGORY_LABEL = {
  internet_subscription: 'Internet Subscription', licensing: 'Licensing', maintenance: 'Maintenance',
  device_replacement: 'Device Replacement', upgrade: 'Upgrade', custom: 'Custom',
};
function currentPeriod() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Geographic hierarchy
const GEO_LEVELS = ['country', 'region', 'district', 'division', 'parish', 'village', 'community'];
const GEO_LEVEL_LABEL = {
  country: 'Country', region: 'Region', district: 'District', division: 'Division',
  parish: 'Parish', village: 'Village', community: 'Community',
};
function geoUnitLabel(u) {
  return u ? `${GEO_LEVEL_LABEL[u.level]}: ${u.name}` : '';
}

// ═════════════════════════════════════════════════════════════════════════════
// ADMIN TAB
// ═════════════════════════════════════════════════════════════════════════════

export function PartnershipAdminTab() {
  const [sub, setSub] = useState('projects');
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try { setStats(await partnershipAPI.adminStats()); } catch (err) { toast.error(errMsg(err)); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Projects" value={stats?.totals?.projects ?? '—'} />
        <StatCard label="Sites" value={stats?.totals?.sites ?? '—'} />
        <StatCard label="Live Sites" value={stats?.totals?.liveSites ?? '—'} color="#22c55e" />
        <StatCard label="Partners" value={stats?.totals?.partners ?? '—'} />
        <StatCard label="Active Partners" value={stats?.totals?.activePartners ?? '—'} color="#22c55e" />
      </div>

      <div className="flex gap-2 border-b border-border">
        {['projects', 'sites', 'partners', 'equipment', 'earnings', 'geography', 'map', 'leads'].map((k) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            className={`px-4 py-2 text-sm font-semibold capitalize border-b-2 transition-colors ${
              sub === k ? 'border-cyan text-cyan' : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {sub === 'projects' && <ProjectsPanel onChanged={loadStats} />}
      {sub === 'sites' && <SitesPanel onChanged={loadStats} />}
      {sub === 'partners' && <PartnersPanel onChanged={loadStats} />}
      {sub === 'equipment' && <EquipmentPanel />}
      {sub === 'earnings' && <EarningsPanel />}
      {sub === 'geography' && <GeographyPanel />}
      {sub === 'map' && <MapPanel />}
      {sub === 'leads' && <LeadsPanel onChanged={loadStats} />}
    </div>
  );
}

// ── Projects ─────────────────────────────────────────────────────────────────
function ProjectsPanel({ onChanged }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // project being edited
  const [form, setForm] = useState({ name: '', region: '', description: '', targetSites: '', minContribution: '', maxContribution: '' });
  const [editForm, setEditForm] = useState({ name: '', region: '', description: '', targetSites: '', minContribution: '', maxContribution: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setProjects((await partnershipAPI.listProjects()).projects); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      await partnershipAPI.createProject({
        ...form, targetSites: Number(form.targetSites) || 0,
        minContribution: form.minContribution === '' ? undefined : Number(form.minContribution),
        maxContribution: form.maxContribution === '' ? undefined : Number(form.maxContribution),
      });
      toast.success('Project created');
      setShowCreate(false);
      setForm({ name: '', region: '', description: '', targetSites: '', minContribution: '', maxContribution: '' });
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to create project')); }
    finally { setSaving(false); }
  };

  const openEdit = (project) => {
    setEditing(project);
    setEditForm({
      name: project.name || '',
      region: project.region || '',
      description: project.description || '',
      targetSites: String(project.targetSites || ''),
      minContribution: String(project.minContribution || ''),
      maxContribution: String(project.maxContribution || ''),
    });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      await partnershipAPI.updateProject(editing._id, {
        ...editForm, targetSites: Number(editForm.targetSites) || 0,
        minContribution: Number(editForm.minContribution) || 0,
        maxContribution: Number(editForm.maxContribution) || 0,
      });
      toast.success('Project updated');
      setEditing(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to update project')); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <SectionHeader title="Projects" action={<Button size="sm" onClick={() => setShowCreate(true)}>+ New Project</Button>} />
      {loading ? <Spinner /> : projects.length === 0 ? (
        <Empty title="No projects yet" description="Create a network zone to get started." />
      ) : (
        <Table headers={['Name', 'Region', 'Status', 'Target Sites', 'Contribution Range', 'Equipment Cost', 'Created', '']}>
          {projects.map((p) => (
            <tr key={p._id}>
              <Td className="font-semibold text-text-primary">{p.name}</Td>
              <Td>{p.region || '—'}</Td>
              <Td><Badge label={p.status} color={p.status === 'active' ? 'green' : 'amber'} /></Td>
              <Td>{p.targetSites}</Td>
              <Td className="text-xs">{fmtUGX(p.minContribution)} – {fmtUGX(p.maxContribution)}</Td>
              <Td>{fmtUGX(p.equipmentSummary?.totalCost)}</Td>
              <Td className="text-text-muted text-xs">{new Date(p.createdAt).toLocaleDateString()}</Td>
              <Td><button className="text-xs text-cyan hover:underline" onClick={() => openEdit(p)}>Edit</button></Td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Project">
        <div className="space-y-3">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mukono" />
          <Input label="Region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. Central" />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Input label="Target Sites" type="number" value={form.targetSites} onChange={(e) => setForm({ ...form, targetSites: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Contribution (UGX)" type="number" value={form.minContribution} onChange={(e) => setForm({ ...form, minContribution: e.target.value })} placeholder="Default: platform min" />
            <Input label="Max Contribution (UGX)" type="number" value={form.maxContribution} onChange={(e) => setForm({ ...form, maxContribution: e.target.value })} placeholder="Default: platform max" />
          </div>
          <Button className="w-full" loading={saving} onClick={create}>Create Project</Button>
        </div>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit — ${editing?.name || ''}`}>
        <div className="space-y-3">
          <Input label="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          <Input label="Region" value={editForm.region} onChange={(e) => setEditForm({ ...editForm, region: e.target.value })} />
          <Input label="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          <Input label="Target Sites" type="number" value={editForm.targetSites} onChange={(e) => setEditForm({ ...editForm, targetSites: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Contribution (UGX)" type="number" value={editForm.minContribution} onChange={(e) => setEditForm({ ...editForm, minContribution: e.target.value })} />
            <Input label="Max Contribution (UGX)" type="number" value={editForm.maxContribution} onChange={(e) => setEditForm({ ...editForm, maxContribution: e.target.value })} />
          </div>
          <Button className="w-full" loading={saving} onClick={saveEdit}>Save Changes</Button>
        </div>
      </Modal>
    </Card>
  );
}

// ── Sites ────────────────────────────────────────────────────────────────────
function SitesPanel({ onChanged }) {
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // site being edited
  const [editForm, setEditForm] = useState({ name: '', location: '', fundingCost: '', revenueSharePct: '', shareTermMonths: '', siteManagerName: '', siteManagerContact: '', gpsLat: '', gpsLng: '', coverageRadius: '', subsiteCode: '' });
  const [assignFor, setAssignFor] = useState(null); // site being assigned a partner
  const [progressFor, setProgressFor] = useState(null); // site getting a progress post
  const [form, setForm] = useState({ name: '', project: '', location: '', fundingCost: '', revenueSharePct: '', shareTermMonths: '', siteManagerName: '', siteManagerContact: '', gpsLat: '', gpsLng: '', coverageRadius: '', subsiteCode: '', geoUnit: '', applyStandardDeployment: false });
  const [geoUnits, setGeoUnits] = useState([]);
  const [geoFor, setGeoFor] = useState(null); // site whose geo unit is being changed
  const [geoUnitPick, setGeoUnitPick] = useState('');
  const [assignForm, setAssignForm] = useState({ partnerId: '', revenueSharePct: '', shareTermMonths: '' });
  const [progressForm, setProgressForm] = useState({ title: '', message: '', updateType: 'general', statusChange: '' });
  const [saving, setSaving] = useState(false);

  // Capacity management — upgrade recording + history
  const [upgradeFor, setUpgradeFor] = useState(null); // site being upgraded
  const [upgradeForm, setUpgradeForm] = useState({ description: '', newCapacity: '', cost: '', notes: '', equipment: [{ ...EMPTY_EQUIPMENT_ROW }] });
  const [historyFor, setHistoryFor] = useState(null); // site whose upgrade history modal is open
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, pt, g] = await Promise.all([
        partnershipAPI.listSites(), partnershipAPI.listProjects(), partnershipAPI.listPartners(), partnershipAPI.listGeoUnits({}),
      ]);
      setSites(s.sites); setProjects(p.projects); setPartners(pt.partners); setGeoUnits(g.units);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedProject = projects.find((p) => p._id === form.project);

  const create = async () => {
    if (!form.name.trim() || !form.project) { toast.error('Name and project are required'); return; }
    setSaving(true);
    try {
      const res = await partnershipAPI.createSite({
        ...form,
        fundingCost: Number(form.fundingCost) || 0,
        revenueSharePct: Number(form.revenueSharePct) || 0,
        shareTermMonths: form.shareTermMonths ? Number(form.shareTermMonths) : null,
        gpsLat: form.gpsLat !== '' ? Number(form.gpsLat) : undefined,
        gpsLng: form.gpsLng !== '' ? Number(form.gpsLng) : undefined,
        coverageRadius: form.coverageRadius !== '' ? Number(form.coverageRadius) : undefined,
        subsiteCode: form.subsiteCode || undefined,
        geoUnit: form.geoUnit || undefined,
      });
      if (form.applyStandardDeployment) {
        await partnershipAPI.applyStandardDeployment(res.site._id);
        toast.success('Site created with the standard deployment applied (30 capacity, ~UGX 3.7M equipment)');
      } else {
        toast.success('Site created');
      }
      if (res.fundingWarning) toast(res.fundingWarning, { icon: '⚠️', duration: 6000 });
      setShowCreate(false);
      setForm({ name: '', project: '', location: '', fundingCost: '', revenueSharePct: '', shareTermMonths: '', siteManagerName: '', siteManagerContact: '', gpsLat: '', gpsLng: '', coverageRadius: '', subsiteCode: '', geoUnit: '', applyStandardDeployment: false });
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to create site')); }
    finally { setSaving(false); }
  };

  const openEdit = (site) => {
    setEditing(site);
    setEditForm({
      name: site.name || '',
      location: site.location || '',
      fundingCost: String(site.fundingCost || ''),
      revenueSharePct: String(site.revenueSharePct || ''),
      shareTermMonths: String(site.shareTermMonths || ''),
      siteManagerName: site.siteManagerName || '',
      siteManagerContact: site.siteManagerContact || '',
      gpsLat: site.gpsLat != null ? String(site.gpsLat) : '',
      gpsLng: site.gpsLng != null ? String(site.gpsLng) : '',
      coverageRadius: site.coverageRadius != null ? String(site.coverageRadius) : '',
      subsiteCode: site.subsiteCode || '',
    });
  };

  const suggestFunding = async () => {
    try {
      const res = await partnershipAPI.suggestFundingCost(editing._id);
      setEditForm({ ...editForm, fundingCost: String(res.suggested) });
      toast.success(`Suggested UGX ${res.suggested.toLocaleString()} (equipment cost ${fmtUGX(res.equipmentCost)} + ${res.marginPct}% margin)`);
    } catch (err) { toast.error(errMsg(err, 'Failed to suggest funding cost')); }
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error('Site name is required'); return; }
    setSaving(true);
    try {
      const res = await partnershipAPI.updateSite(editing._id, {
        name: editForm.name,
        location: editForm.location,
        fundingCost: Number(editForm.fundingCost) || 0,
        revenueSharePct: Number(editForm.revenueSharePct) || 0,
        shareTermMonths: editForm.shareTermMonths ? Number(editForm.shareTermMonths) : null,
        siteManagerName: editForm.siteManagerName || null,
        siteManagerContact: editForm.siteManagerContact || null,
        gpsLat: editForm.gpsLat !== '' ? Number(editForm.gpsLat) : null,
        gpsLng: editForm.gpsLng !== '' ? Number(editForm.gpsLng) : null,
        coverageRadius: editForm.coverageRadius !== '' ? Number(editForm.coverageRadius) : null,
        subsiteCode: editForm.subsiteCode || null,
      });
      toast.success('Site updated');
      if (res.fundingWarning) toast(res.fundingWarning, { icon: '⚠️', duration: 6000 });
      setEditing(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to update site')); }
    finally { setSaving(false); }
  };

  const openGeo = (site) => {
    setGeoFor(site);
    setGeoUnitPick(site.geoUnit?._id || '');
  };

  const changeGeo = async () => {
    setSaving(true);
    try {
      await partnershipAPI.assignSiteGeo(geoFor._id, geoUnitPick || null);
      toast.success(geoUnitPick ? 'Geo unit assigned' : 'Geo unit cleared');
      setGeoFor(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to change geo unit')); }
    finally { setSaving(false); }
  };

  const openUpgrade = (site) => {
    setUpgradeFor(site);
    setUpgradeForm({ description: '', newCapacity: String((site.currentCapacity || 0) + 20), cost: '', notes: '', equipment: [{ ...EMPTY_EQUIPMENT_ROW }] });
  };

  const updateEquipmentRow = (idx, patch) => {
    setUpgradeForm((f) => ({ ...f, equipment: f.equipment.map((row, i) => (i === idx ? { ...row, ...patch } : row)) }));
  };
  const addEquipmentRow = () => setUpgradeForm((f) => ({ ...f, equipment: [...f.equipment, { ...EMPTY_EQUIPMENT_ROW }] }));
  const removeEquipmentRow = (idx) => setUpgradeForm((f) => ({ ...f, equipment: f.equipment.filter((_, i) => i !== idx) }));

  const submitUpgrade = async () => {
    if (!upgradeForm.description.trim()) { toast.error('Description is required'); return; }
    const newCapacity = Number(upgradeForm.newCapacity);
    if (!newCapacity || newCapacity <= (upgradeFor.currentCapacity || 0)) {
      toast.error(`New capacity must be greater than the current capacity (${upgradeFor.currentCapacity || 0})`);
      return;
    }
    const equipment = upgradeForm.equipment
      .filter((r) => r.type.trim() && r.unitPrice !== '')
      .map((r) => ({ type: r.type.trim(), quantity: Number(r.quantity) || 1, unitPrice: Number(r.unitPrice) || 0 }));

    setSaving(true);
    try {
      await partnershipAPI.recordUpgrade(upgradeFor._id, {
        description: upgradeForm.description,
        newCapacity,
        cost: upgradeForm.cost !== '' ? Number(upgradeForm.cost) : undefined,
        notes: upgradeForm.notes || undefined,
        equipment,
      });
      toast.success(`Capacity raised to ${newCapacity}`);
      setUpgradeFor(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to record upgrade')); }
    finally { setSaving(false); }
  };

  const openHistory = async (site) => {
    setHistoryFor(site);
    setHistoryLoading(true);
    try {
      const res = await partnershipAPI.listSiteUpgrades(site._id);
      setHistory(res.upgrades);
    } catch (err) { toast.error(errMsg(err, 'Failed to load upgrade history')); }
    finally { setHistoryLoading(false); }
  };

  const applyStandardDeploymentToExisting = async (site) => {
    if (!window.confirm(`Apply the standard deployment preset to "${site.name}"? This adds 30 customer routers, 1 MikroTik, 2 CAT6 rolls, and accessories (~UGX 3.7M) as equipment, and sets capacity to at least 30.`)) return;
    try {
      await partnershipAPI.applyStandardDeployment(site._id);
      toast.success('Standard deployment applied');
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to apply standard deployment')); }
  };

  const assign = async () => {
    if (!assignForm.partnerId) { toast.error('Select a partner'); return; }
    setSaving(true);
    try {
      const res = await partnershipAPI.assignPartner(assignFor._id, {
        partnerId: assignForm.partnerId,
        revenueSharePct: Number(assignForm.revenueSharePct) || 0,
        shareTermMonths: assignForm.shareTermMonths ? Number(assignForm.shareTermMonths) : null,
      });
      toast.success('Partner assigned');
      if (res.localityWarning) toast(res.localityWarning, { icon: '⚠️', duration: 7000 });
      setAssignFor(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to assign partner')); }
    finally { setSaving(false); }
  };

  const unassignPartner = async (site) => {
    if (!window.confirm(`Unassign the partner from "${site.name}"? The site goes back to available; the partner keeps any other sites they manage.`)) return;
    try {
      await partnershipAPI.unassignPartner(site._id);
      toast.success('Partner unassigned — site is available again');
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to unassign partner')); }
  };

  const advance = async (site, status) => {
    try {
      await partnershipAPI.advanceSiteStatus(site._id, status);
      toast.success(`Site status: ${SITE_STATUS_LABEL[status]}`);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to update status')); }
  };

  const postProgress = async () => {
    if (!progressForm.title.trim() || !progressForm.message.trim()) { toast.error('Title and message are required'); return; }
    setSaving(true);
    try {
      await partnershipAPI.postProgress(progressFor._id, {
        title: progressForm.title,
        message: progressForm.message,
        updateType: progressForm.updateType,
        statusChange: progressForm.statusChange || undefined,
      });
      toast.success('Progress update posted' + (progressForm.statusChange ? ` — status advanced to ${SITE_STATUS_LABEL[progressForm.statusChange]}` : ''));
      setProgressFor(null);
      setProgressForm({ title: '', message: '', updateType: 'general', statusChange: '' });
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to post update')); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <SectionHeader title="Sites" action={<Button size="sm" onClick={() => setShowCreate(true)}>+ New Site</Button>} />
      {loading ? <Spinner /> : sites.length === 0 ? (
        <Empty title="No sites yet" description="Add a site to a project." />
      ) : (
        <Table headers={['Name', 'Project', 'Availability', 'Status', 'Partner', 'Funding Cost', 'Share %', 'Equipment Cost', 'Capacity', 'Location', 'Actions']}>
          {sites.map((s) => (
            <tr key={s._id}>
              <Td className="font-semibold text-text-primary">{s.name}</Td>
              <Td>{s.project?.name || '—'}</Td>
              <Td><Badge label={s.available ? 'Available' : 'Taken'} color={s.available ? 'green' : 'amber'} /></Td>
              <Td><Badge label={SITE_STATUS_LABEL[s.status]} color={SITE_STATUS_COLOR[s.status]} /></Td>
              <Td>{s.partner?.name || <span className="text-text-muted">Unassigned</span>}</Td>
              <Td>{fmtUGX(s.fundingCost)}</Td>
              <Td>{s.revenueSharePct}%</Td>
              <Td>{fmtUGX(s.equipmentSummary?.totalCost)}</Td>
              <Td className="text-xs">
                <div className="flex flex-col gap-0.5">
                  <span>{s.activeCustomers ?? 0} / {s.currentCapacity ?? 0} customers</span>
                  <Badge label={`${s.utilizationPct ?? 0}% utilized`} color={utilizationColor(s.utilizationPct ?? 0)} />
                </div>
              </Td>
              <Td className="text-xs">
                <div className="flex flex-col gap-0.5">
                  <span>{s.geoUnit ? geoUnitLabel(s.geoUnit) : <span className="text-text-muted">No geo unit</span>}</span>
                  {s.subsiteCode && <span className="text-text-muted font-mono">{s.subsiteCode}</span>}
                </div>
              </Td>
              <Td>
                <div className="flex gap-1.5 flex-wrap">
                  <button className="text-xs text-cyan hover:underline" onClick={() => openEdit(s)}>Edit</button>
                  {!s.partner ? (
                    <button className="text-xs text-cyan hover:underline" onClick={() => { setAssignFor(s); setAssignForm({ partnerId: '', revenueSharePct: String(s.revenueSharePct || ''), shareTermMonths: String(s.shareTermMonths || '') }); }}>
                      Assign Partner
                    </button>
                  ) : (
                    <button className="text-xs text-amber hover:underline" onClick={() => unassignPartner(s)}>Unassign</button>
                  )}
                  <Select
                    value=""
                    onChange={(e) => e.target.value && advance(s, e.target.value)}
                    className="text-xs"
                  >
                    <option value="">Advance status…</option>
                    {SITE_STATUSES.filter((st) => st !== s.status).map((st) => (
                      <option key={st} value={st}>{SITE_STATUS_LABEL[st]}</option>
                    ))}
                  </Select>
                  <button className="text-xs text-cyan hover:underline" onClick={() => setProgressFor(s)}>+ Update</button>
                  <button className="text-xs text-purple hover:underline" onClick={() => openUpgrade(s)}>Record Upgrade</button>
                  <button className="text-xs text-text-muted hover:underline" onClick={() => openHistory(s)}>History</button>
                  <button className="text-xs text-cyan hover:underline" onClick={() => openGeo(s)}>Set Geo Unit</button>
                  {!s.currentCapacity && (
                    <button className="text-xs text-cyan hover:underline" onClick={() => applyStandardDeploymentToExisting(s)}>Apply Standard Deployment</button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Site">
        <div className="space-y-3">
          <Select label="Project" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
            <option value="">Select project…</option>
            {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>
          <Input label="Site Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <Input
            label="Funding Cost (UGX) — what a partner contributes"
            type="number" value={form.fundingCost}
            onChange={(e) => setForm({ ...form, fundingCost: e.target.value })}
            note={selectedProject ? `Project range: ${fmtUGX(selectedProject.minContribution)} – ${fmtUGX(selectedProject.maxContribution)}` : undefined}
          />
          <Input label="Revenue Share % (partner's cut)" type="number" value={form.revenueSharePct} onChange={(e) => setForm({ ...form, revenueSharePct: e.target.value })} />
          <Input label="Share Term (months)" type="number" value={form.shareTermMonths} onChange={(e) => setForm({ ...form, shareTermMonths: e.target.value })} />
          <Input label="Site Manager Name (optional)" value={form.siteManagerName} onChange={(e) => setForm({ ...form, siteManagerName: e.target.value })} placeholder="Defaults to the assigned partner" />
          <Input label="Site Manager Contact (optional)" value={form.siteManagerContact} onChange={(e) => setForm({ ...form, siteManagerContact: e.target.value })} placeholder="Phone or email" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="GPS Latitude (optional)" type="number" value={form.gpsLat} onChange={(e) => setForm({ ...form, gpsLat: e.target.value })} placeholder="-90 to 90" />
            <Input label="GPS Longitude (optional)" type="number" value={form.gpsLng} onChange={(e) => setForm({ ...form, gpsLng: e.target.value })} placeholder="-180 to 180" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Coverage Radius (m, optional)" type="number" value={form.coverageRadius} onChange={(e) => setForm({ ...form, coverageRadius: e.target.value })} />
            <Input label="Subsite Code (optional)" value={form.subsiteCode} onChange={(e) => setForm({ ...form, subsiteCode: e.target.value })} placeholder="e.g. KN-001" />
          </div>
          <Select label="Geo Unit (optional)" value={form.geoUnit} onChange={(e) => setForm({ ...form, geoUnit: e.target.value })}>
            <option value="">Not assigned</option>
            {geoUnits.map((u) => <option key={u._id} value={u._id}>{geoUnitLabel(u)}</option>)}
          </Select>
          <label className="flex items-start gap-2 text-xs text-text-secondary bg-bg-panel border border-border rounded-lg px-3 py-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={form.applyStandardDeployment} onChange={(e) => setForm({ ...form, applyStandardDeployment: e.target.checked })} />
            <span>Apply standard deployment preset — 30 customer routers, 1 MikroTik, 2 CAT6 rolls, install accessories (~UGX 3,700,000). Sets capacity to 30; every line stays editable afterward.</span>
          </label>
          <Button className="w-full" loading={saving} onClick={create}>Create Site</Button>
        </div>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit — ${editing?.name || ''}`}>
        <div className="space-y-3">
          <Input label="Site Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          <Input label="Location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
          <div className="flex items-end gap-2">
            <Input
              label="Funding Cost (UGX)" type="number" value={editForm.fundingCost}
              onChange={(e) => setEditForm({ ...editForm, fundingCost: e.target.value })}
              note={editing?.project ? `Project range: ${fmtUGX(editing.project.minContribution)} – ${fmtUGX(editing.project.maxContribution)}` : undefined}
              className="flex-1"
            />
            <Button variant="ghost" size="sm" onClick={suggestFunding}>Suggest</Button>
          </div>
          <Input label="Revenue Share %" type="number" value={editForm.revenueSharePct} onChange={(e) => setEditForm({ ...editForm, revenueSharePct: e.target.value })} />
          <Input label="Share Term (months)" type="number" value={editForm.shareTermMonths} onChange={(e) => setEditForm({ ...editForm, shareTermMonths: e.target.value })} />
          <Input label="Site Manager Name" value={editForm.siteManagerName} onChange={(e) => setEditForm({ ...editForm, siteManagerName: e.target.value })} placeholder="Defaults to the assigned partner" />
          <Input label="Site Manager Contact" value={editForm.siteManagerContact} onChange={(e) => setEditForm({ ...editForm, siteManagerContact: e.target.value })} placeholder="Phone or email" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="GPS Latitude" type="number" value={editForm.gpsLat} onChange={(e) => setEditForm({ ...editForm, gpsLat: e.target.value })} placeholder="-90 to 90" />
            <Input label="GPS Longitude" type="number" value={editForm.gpsLng} onChange={(e) => setEditForm({ ...editForm, gpsLng: e.target.value })} placeholder="-180 to 180" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Coverage Radius (m)" type="number" value={editForm.coverageRadius} onChange={(e) => setEditForm({ ...editForm, coverageRadius: e.target.value })} />
            <Input label="Subsite Code" value={editForm.subsiteCode} onChange={(e) => setEditForm({ ...editForm, subsiteCode: e.target.value })} placeholder="e.g. KN-001" />
          </div>
          <Button className="w-full" loading={saving} onClick={saveEdit}>Save Changes</Button>
        </div>
      </Modal>

      <Modal open={!!geoFor} onClose={() => setGeoFor(null)} title={`Set Geo Unit — ${geoFor?.name || ''}`}>
        <div className="space-y-3">
          <Select label="Geo Unit" value={geoUnitPick} onChange={(e) => setGeoUnitPick(e.target.value)}>
            <option value="">Not assigned</option>
            {geoUnits.map((u) => <option key={u._id} value={u._id}>{geoUnitLabel(u)}</option>)}
          </Select>
          <p className="text-xs text-text-muted">Manage the hierarchy itself (create Countries, Regions, Districts…) from the Geography tab.</p>
          <Button className="w-full" loading={saving} onClick={changeGeo}>Save</Button>
        </div>
      </Modal>

      <Modal open={!!assignFor} onClose={() => setAssignFor(null)} title={`Assign Partner — ${assignFor?.name || ''}`}>
        <div className="space-y-3">
          <Select label="Partner" value={assignForm.partnerId} onChange={(e) => setAssignForm({ ...assignForm, partnerId: e.target.value })}>
            <option value="">Select partner…</option>
            {partners.map((p) => <option key={p._id} value={p._id}>{p.name} ({p.status})</option>)}
          </Select>
          <Input label="Revenue Share %" type="number" value={assignForm.revenueSharePct} onChange={(e) => setAssignForm({ ...assignForm, revenueSharePct: e.target.value })} />
          <Input label="Share Term (months)" type="number" value={assignForm.shareTermMonths} onChange={(e) => setAssignForm({ ...assignForm, shareTermMonths: e.target.value })} />
          <Button className="w-full" loading={saving} onClick={assign}>Assign</Button>
        </div>
      </Modal>

      <Modal open={!!progressFor} onClose={() => setProgressFor(null)} title={`Post Update — ${progressFor?.name || ''}`}>
        <div className="space-y-3">
          <Input label="Title" value={progressForm.title} onChange={(e) => setProgressForm({ ...progressForm, title: e.target.value })} placeholder="e.g. Went live" />
          <Input label="Message" value={progressForm.message} onChange={(e) => setProgressForm({ ...progressForm, message: e.target.value })} placeholder="e.g. 500 customers connected" />
          <Select label="Update type" value={progressForm.updateType} onChange={(e) => setProgressForm({ ...progressForm, updateType: e.target.value })}>
            {UPDATE_TYPES.map((t) => <option key={t} value={t}>{UPDATE_TYPE_LABEL[t]}</option>)}
          </Select>
          <Select label="Also advance status to… (optional)" value={progressForm.statusChange} onChange={(e) => setProgressForm({ ...progressForm, statusChange: e.target.value })}>
            <option value="">No status change</option>
            {SITE_STATUSES.filter((st) => st !== progressFor?.status).map((st) => (
              <option key={st} value={st}>{SITE_STATUS_LABEL[st]}</option>
            ))}
          </Select>
          <Button className="w-full" loading={saving} onClick={postProgress}>Post Update</Button>
        </div>
      </Modal>

      <Modal open={!!upgradeFor} onClose={() => setUpgradeFor(null)} title={`Record Upgrade — ${upgradeFor?.name || ''}`}>
        <div className="space-y-3">
          <p className="text-xs text-text-muted">Current capacity: <span className="text-text-primary font-semibold">{upgradeFor?.currentCapacity || 0}</span></p>
          <Input label="Description" value={upgradeForm.description} onChange={(e) => setUpgradeForm({ ...upgradeForm, description: e.target.value })} placeholder="e.g. Expand to 50 — added 20 more customer routers" />
          <Input label="New Capacity" type="number" value={upgradeForm.newCapacity} onChange={(e) => setUpgradeForm({ ...upgradeForm, newCapacity: e.target.value })} />

          <div>
            <p className="text-xs font-semibold text-text-primary mb-1.5">Equipment added (optional — cost auto-sums from these)</p>
            <div className="space-y-2">
              {upgradeForm.equipment.map((row, idx) => (
                <div key={idx} className="flex gap-1.5 items-center">
                  <input
                    className="flex-1 text-xs bg-bg-panel border border-border rounded-lg px-2 py-1.5"
                    placeholder="Type (e.g. Customer Router)"
                    value={row.type}
                    onChange={(e) => updateEquipmentRow(idx, { type: e.target.value })}
                  />
                  <input
                    className="w-16 text-xs bg-bg-panel border border-border rounded-lg px-2 py-1.5"
                    type="number" placeholder="Qty"
                    value={row.quantity}
                    onChange={(e) => updateEquipmentRow(idx, { quantity: e.target.value })}
                  />
                  <input
                    className="w-24 text-xs bg-bg-panel border border-border rounded-lg px-2 py-1.5"
                    type="number" placeholder="Unit price"
                    value={row.unitPrice}
                    onChange={(e) => updateEquipmentRow(idx, { unitPrice: e.target.value })}
                  />
                  {upgradeForm.equipment.length > 1 && (
                    <button className="text-red text-xs" onClick={() => removeEquipmentRow(idx)}>✕</button>
                  )}
                </div>
              ))}
            </div>
            <button className="text-xs text-cyan hover:underline mt-1.5" onClick={addEquipmentRow}>+ Add another item</button>
          </div>

          <Input label="Cost override (UGX, optional — leave blank to auto-sum equipment)" type="number" value={upgradeForm.cost} onChange={(e) => setUpgradeForm({ ...upgradeForm, cost: e.target.value })} />
          <Input label="Notes (optional)" value={upgradeForm.notes} onChange={(e) => setUpgradeForm({ ...upgradeForm, notes: e.target.value })} />
          <Button className="w-full" loading={saving} onClick={submitUpgrade}>Record Upgrade</Button>
        </div>
      </Modal>

      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={`Upgrade History — ${historyFor?.name || ''}`}>
        {historyLoading ? <Spinner /> : history.length === 0 ? (
          <Empty title="No upgrades recorded yet" description="This site is still on its original deployment." />
        ) : (
          <ul className="space-y-3">
            {history.map((h) => (
              <li key={h._id} className="text-xs bg-bg-panel border border-border rounded-lg px-3 py-2.5">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-text-primary font-semibold">{h.description}</span>
                  <span className="text-text-muted">{new Date(h.date).toLocaleDateString()}</span>
                </div>
                <div className="text-text-secondary">
                  Capacity {h.previousCapacity} → <span className="text-cyan font-semibold">{h.newCapacity}</span> · {fmtUGX(h.cost)}
                  {h.approvedBy?.name && <> · approved by {h.approvedBy.name}</>}
                </div>
                {h.equipmentAssetIds?.length > 0 && (
                  <div className="text-text-muted mt-1">
                    {h.equipmentAssetIds.map((e) => `${e.type} ×${e.quantity}`).join(', ')}
                  </div>
                )}
                {h.notes && <div className="text-text-muted mt-1 italic">{h.notes}</div>}
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </Card>
  );
}

// ── Partners ─────────────────────────────────────────────────────────────────
function CopyLinkButton({ link }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — copy the link manually');
    }
  };
  return (
    <Button size="sm" variant={copied ? 'secondary' : 'primary'} onClick={copy}>
      {copied ? 'Copied!' : 'Copy Link'}
    </Button>
  );
}

function PartnersPanel({ onChanged }) {
  const [partners, setPartners] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', contributionAmount: '', siteId: '' });
  const [saving, setSaving] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // { partner, inviteToken, site } shown once for manual delivery
  const [viewing, setViewing] = useState(null); // { partner, earningsSummary } for the detail modal
  const [viewLoading, setViewLoading] = useState(false);
  const [dupeCheck, setDupeCheck] = useState(null); // { status, partner|user } from checkPartnerDuplicate, or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch ALL sites, not just available ones — a partner's own site(s)
      // are by definition "taken", so an available-only list would never
      // show what they already manage.
      const [p, s] = await Promise.all([partnershipAPI.listPartners(), partnershipAPI.listSites()]);
      setPartners(p.partners); setAllSites(s.sites);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const availableSites = allSites.filter((s) => s.available);
  const sitesForPartner = (partnerId) => allSites.filter((s) => (s.partner?._id || s.partner) === partnerId);
  const selectedSite = availableSites.find((s) => s._id === form.siteId);

  const pickSite = (siteId) => {
    const site = availableSites.find((s) => s._id === siteId);
    setForm({ ...form, siteId, contributionAmount: site ? String(site.fundingCost || '') : form.contributionAmount });
  };

  const openView = async (partner) => {
    setViewLoading(true);
    setViewing({ partner, earningsSummary: null });
    try {
      const res = await partnershipAPI.getPartner(partner._id);
      setViewing({ partner: res.partner, earningsSummary: res.earningsSummary });
    } catch (err) { toast.error(errMsg(err, 'Failed to load partner details')); setViewing(null); }
    finally { setViewLoading(false); }
  };

  // Two-step confirm: a plain delete first; if the backend reports the
  // partner has earnings (409), the error message names the exact count and
  // total, so a second confirm shows the real numbers before retrying with
  // force — never silently destroys financial history.
  const deletePartner = async (partner) => {
    if (!window.confirm(`Delete partner "${partner.name}"? Any site(s) they manage will be released back to available.`)) return;
    try {
      await partnershipAPI.deletePartner(partner._id);
      toast.success('Partner deleted');
      load(); onChanged?.();
    } catch (err) {
      if (err.status === 409) {
        if (window.confirm(`${err.message}\n\nDelete anyway?`)) {
          try {
            await partnershipAPI.deletePartner(partner._id, true);
            toast.success('Partner deleted');
            load(); onChanged?.();
          } catch (err2) { toast.error(errMsg(err2, 'Failed to delete partner')); }
        }
      } else {
        toast.error(errMsg(err, 'Failed to delete partner'));
      }
    }
  };

  const revokeInvite = async (partner) => {
    if (!window.confirm(`Revoke the pending invite for "${partner.name}"? This frees their site (if any) and removes them.`)) return;
    try {
      await partnershipAPI.revokeInvite(partner._id);
      toast.success('Invite revoked');
      load(); onChanged?.();
    } catch (err) {
      if (err.status === 409) {
        if (window.confirm(`${err.message}\n\nRevoke anyway?`)) {
          try {
            await partnershipAPI.revokeInvite(partner._id, true);
            toast.success('Invite revoked');
            load(); onChanged?.();
          } catch (err2) { toast.error(errMsg(err2, 'Failed to revoke invite')); }
        }
      } else {
        toast.error(errMsg(err, 'Failed to revoke invite'));
      }
    }
  };

  const resendInvite = async (partner) => {
    try {
      const res = await partnershipAPI.resendInvite(partner._id);
      toast.success('Invite resent — the old link no longer works');
      setLastInvite(res);
      setShowInvite(false);
      setDupeCheck(null);
      setForm({ name: '', phone: '', email: '', contributionAmount: '', siteId: '' });
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to resend invite')); }
  };

  // Checks for an existing partner/customer on this email or phone BEFORE
  // attempting the invite, so the admin gets the right next action (resend /
  // assign-a-site / nothing-to-worry-about) instead of a bare error. A
  // 'customer' match isn't blocked — invitePartner links to that account —
  // so it falls straight through to the normal invite call.
  const invite = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error('Name, phone, and email are all required'); return;
    }
    setSaving(true);
    try {
      const dupe = await partnershipAPI.checkPartnerDuplicate({ email: form.email, phone: form.phone });
      if (dupe.status === 'pending' || dupe.status === 'active' || dupe.status === 'staff') {
        setDupeCheck(dupe);
        return;
      }
      const res = await partnershipAPI.invitePartner({
        name: form.name, phone: form.phone, email: form.email,
        contributionAmount: Number(form.contributionAmount) || 0,
        siteId: form.siteId || undefined,
      });
      toast.success(res.linkedExistingUser ? 'Existing customer account linked and invited as partner' : 'Partner invited');
      setLastInvite(res);
      setShowInvite(false);
      setDupeCheck(null);
      setForm({ name: '', phone: '', email: '', contributionAmount: '', siteId: '' });
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to invite partner')); }
    finally { setSaving(false); }
  };

  const inviteLink = lastInvite ? `${window.location.origin}/partner-invite?token=${lastInvite.inviteToken}` : '';

  return (
    <Card>
      <SectionHeader title="Partners" action={<Button size="sm" onClick={() => setShowInvite(true)}>+ Invite Partner</Button>} />

      {lastInvite && (
        <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 mb-4 text-xs">
          <p className="font-semibold text-amber mb-1">
            Invite created for {lastInvite.partner.name}
            {lastInvite.site ? ` — funding "${lastInvite.site.name}"` : ''} — deliver this link manually (SMS/email/call):
          </p>
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 break-all text-text-secondary bg-bg-panel p-2 rounded-lg">{inviteLink}</code>
            <CopyLinkButton link={inviteLink} />
          </div>
          <button className="text-cyan mt-2 hover:underline" onClick={() => setLastInvite(null)}>Dismiss</button>
        </div>
      )}

      {loading ? <Spinner /> : partners.length === 0 ? (
        <Empty title="No partners yet" description="Invite a partner to a site." />
      ) : (
        <Table headers={['Name', 'Contact', 'Sites Managed', 'Contribution', 'Status', 'Invited', '']}>
          {partners.map((p) => {
            const partnerSites = sitesForPartner(p._id);
            return (
              <tr key={p._id}>
                <Td className="font-semibold text-text-primary">{p.name}</Td>
                <Td className="text-xs">{p.email}<br />{p.phone}</Td>
                <Td className="text-xs">
                  {partnerSites.length === 0 ? <span className="text-text-muted">—</span>
                    : partnerSites.length === 1 ? partnerSites[0].name
                    : `${partnerSites.length} sites`}
                </Td>
                <Td>{fmtUGX(p.contributionAmount)}</Td>
                <Td><Badge label={p.status} color={p.status === 'active' ? 'green' : 'amber'} /></Td>
                <Td className="text-text-muted text-xs">{new Date(p.createdAt).toLocaleDateString()}</Td>
                <Td>
                  <div className="flex gap-2 flex-wrap">
                    <button className="text-xs text-cyan hover:underline" onClick={() => openView(p)}>View</button>
                    {p.status === 'invited' && (
                      <>
                        <button className="text-xs text-cyan hover:underline" onClick={() => resendInvite(p)}>Resend</button>
                        <button className="text-xs text-amber hover:underline" onClick={() => revokeInvite(p)}>Revoke</button>
                      </>
                    )}
                    <button className="text-xs text-red hover:underline" onClick={() => deletePartner(p)}>Delete</button>
                  </div>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      <Modal open={showInvite} onClose={() => { setShowInvite(false); setDupeCheck(null); }} title="Invite Partner">
        <div className="space-y-3">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Phone" value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); setDupeCheck(null); }} />
          <Input label="Email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setDupeCheck(null); }} />

          <Select label="Site they're funding (optional)" value={form.siteId} onChange={(e) => pickSite(e.target.value)}>
            <option value="">No site — invite only</option>
            {availableSites.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.project?.name})</option>)}
          </Select>

          {selectedSite && (
            <div className="bg-bg-panel border border-border rounded-xl p-3 text-xs space-y-1">
              <p className="font-semibold text-text-primary">{selectedSite.name} — {selectedSite.project?.name}</p>
              <p className="text-text-secondary">Funding cost: <span className="font-semibold">{fmtUGX(selectedSite.fundingCost)}</span></p>
              <p className="text-text-secondary">Revenue share: <span className="font-semibold">{selectedSite.revenueSharePct}%</span>{selectedSite.shareTermMonths ? ` for ${selectedSite.shareTermMonths} months` : ''}</p>
              <p className="text-text-muted">This is what the partner will be committing to. Confirm before sending.</p>
            </div>
          )}

          <Input label="Contribution Amount (UGX)" type="number" value={form.contributionAmount} onChange={(e) => setForm({ ...form, contributionAmount: e.target.value })} />

          {dupeCheck?.status === 'pending' && (
            <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 text-xs space-y-2">
              <p className="text-amber font-semibold">{dupeCheck.partner.name} already has a pending invite on this email/phone.</p>
              <p className="text-text-muted">Resend it instead of creating a duplicate — this issues a fresh link and invalidates the old one.</p>
              <Button size="sm" className="w-full" onClick={() => resendInvite(dupeCheck.partner)}>Resend Their Invite</Button>
            </div>
          )}

          {dupeCheck?.status === 'active' && (
            <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 text-xs space-y-2">
              <p className="text-amber font-semibold">{dupeCheck.partner.name} is already an active partner on this email/phone.</p>
              <p className="text-text-muted">Assign them an additional site from the Sites tab instead of inviting a duplicate.</p>
              <Button size="sm" variant="secondary" className="w-full" onClick={() => { setShowInvite(false); setDupeCheck(null); openView(dupeCheck.partner); }}>View Partner</Button>
            </div>
          )}

          {dupeCheck?.status === 'staff' && (
            <div className="bg-red/10 border border-red/25 rounded-xl p-3 text-xs space-y-1">
              <p className="text-red font-semibold">This email/phone belongs to an existing {dupeCheck.user.role} account.</p>
              <p className="text-text-muted">It can't be converted into a partner — use a different email/phone for this invite.</p>
            </div>
          )}

          {(!dupeCheck || dupeCheck.status === 'none') && (
            <Button className="w-full" loading={saving} onClick={invite}>Send Invite</Button>
          )}
          <p className="text-text-muted text-xs text-center">
            Need to give this partner an additional site later? Assign it from the Sites tab — one partner can manage more than one site.
          </p>
        </div>
      </Modal>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.partner?.name || 'Partner'} size="lg">
        {viewLoading || !viewing?.partner?.sites ? <Spinner /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Sites Managed" value={viewing.partner.sitesSummary?.siteCount ?? 0} />
              <StatCard label="Total Contributed" value={fmtUGX(viewing.partner.sitesSummary?.totalFundingCost)} />
              <StatCard label="Total Earned" value={fmtUGX(viewing.earningsSummary?.totalEarnedUGX)} color="#22c55e" />
            </div>
            {viewing.partner.sites.length === 0 ? (
              <Empty title="No sites assigned yet" description="Assign a site to this partner from the Sites tab." />
            ) : (
              <Table headers={['Site', 'Project', 'Status', 'Funding Cost', 'Share %', 'Equipment Cost']}>
                {viewing.partner.sites.map((s) => (
                  <tr key={s._id}>
                    <Td className="font-semibold text-text-primary">{s.name}</Td>
                    <Td>{s.project?.name || '—'}</Td>
                    <Td><Badge label={SITE_STATUS_LABEL[s.status]} color={SITE_STATUS_COLOR[s.status]} /></Td>
                    <Td>{fmtUGX(s.fundingCost)}</Td>
                    <Td>{s.revenueSharePct}%</Td>
                    <Td>{fmtUGX(s.equipmentSummary?.totalCost)}</Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}

// ── Leads (Partner Interest) ────────────────────────────────────────────────
const LEAD_STATUSES = ['new', 'contacted', 'invited', 'declined'];
const LEAD_STATUS_COLOR = { new: 'blue', contacted: 'amber', invited: 'green', declined: 'text-muted' };

function LeadsPanel({ onChanged }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [area, setArea] = useState('');
  const [status, setStatus] = useState('');
  const [allSites, setAllSites] = useState([]);
  const [editing, setEditing] = useState(null); // lead being reviewed (status/internalNotes modal)
  const [editForm, setEditForm] = useState({ status: '', internalNotes: '' });
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(null); // lead being converted to a partner invite
  const [convertForm, setConvertForm] = useState({ siteId: '', contributionAmount: '', email: '' });
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        partnershipAPI.listLeads({ search: search || undefined, area: area || undefined, status: status || undefined }),
        partnershipAPI.listSites(),
      ]);
      setLeads(l.leads); setAllSites(s.sites);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [search, area, status]);

  useEffect(() => { load(); }, [load]);

  const availableSites = allSites.filter((s) => s.available);
  const selectedSite = availableSites.find((s) => s._id === convertForm.siteId);

  const openEdit = (lead) => {
    setEditing(lead);
    setEditForm({ status: lead.status, internalNotes: lead.internalNotes || '' });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await partnershipAPI.updateLead(editing._id, editForm);
      toast.success('Lead updated');
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to update lead')); }
    finally { setSaving(false); }
  };

  const openConvert = (lead) => {
    setConverting(lead);
    setConvertForm({ siteId: '', contributionAmount: '', email: lead.email || '' });
  };

  const pickConvertSite = (siteId) => {
    const site = availableSites.find((s) => s._id === siteId);
    setConvertForm({ siteId, contributionAmount: site ? String(site.fundingCost || '') : convertForm.contributionAmount });
  };

  const convert = async () => {
    if (!convertForm.email.trim()) {
      toast.error('An email is required to send the invite'); return;
    }
    setSaving(true);
    try {
      const res = await partnershipAPI.convertLead(converting._id, {
        siteId: convertForm.siteId || undefined,
        contributionAmount: Number(convertForm.contributionAmount) || undefined,
        email: convertForm.email.trim(),
      });
      toast.success('Lead converted — partner invited');
      setLastInvite(res);
      setConverting(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err, 'Failed to convert lead')); }
    finally { setSaving(false); }
  };

  const inviteLink = lastInvite ? `${window.location.origin}/partner-invite?token=${lastInvite.inviteToken}` : '';

  return (
    <Card>
      <SectionHeader title="Leads — Partner Interest" />

      {lastInvite && (
        <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 mb-4 text-xs">
          <p className="font-semibold text-amber mb-1">
            Invite created for {lastInvite.partner.name}
            {lastInvite.site ? ` — funding "${lastInvite.site.name}"` : ''} — deliver this link manually (SMS/email/call):
          </p>
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 break-all text-text-secondary bg-bg-panel p-2 rounded-lg">{inviteLink}</code>
            <CopyLinkButton link={inviteLink} />
          </div>
          <button className="text-cyan mt-2 hover:underline" onClick={() => setLastInvite(null)}>Dismiss</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-4">
        <input
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-text-primary"
          placeholder="Search name, phone, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <input
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-text-primary"
          placeholder="Filter by area…"
          value={area}
          onChange={(e) => setArea(e.target.value)}
        />
        <select
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-text-primary"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : leads.length === 0 ? (
        <Empty title="No leads yet" description="Leads submitted from /partner-interest will show up here." />
      ) : (
        <Table headers={['Name', 'Contact', 'Area', 'Approx. Contribution', 'Status', 'Submitted', '']}>
          {leads.map((l) => (
            <tr key={l._id}>
              <Td className="font-semibold text-text-primary">{l.name}</Td>
              <Td className="text-xs">{l.phone}{l.email ? <><br />{l.email}</> : null}</Td>
              <Td>{l.area}</Td>
              <Td className="text-xs">{l.approxContribution || <span className="text-text-muted">—</span>}</Td>
              <Td><Badge label={l.status} color={LEAD_STATUS_COLOR[l.status]} /></Td>
              <Td className="text-text-muted text-xs">{new Date(l.createdAt).toLocaleDateString()}</Td>
              <Td>
                <div className="flex gap-2 flex-wrap">
                  <button className="text-xs text-cyan hover:underline" onClick={() => openEdit(l)}>Review</button>
                  {l.convertedToPartner ? (
                    <span className="text-xs text-text-muted">Converted</span>
                  ) : (
                    <button className="text-xs text-green hover:underline" onClick={() => openConvert(l)}>Invite as Partner</button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.name || 'Lead'}>
        {editing && (
          <div className="space-y-3">
            {editing.notes && (
              <div className="bg-bg-panel border border-border rounded-xl p-3 text-xs">
                <p className="text-text-muted mb-1">Why they're interested:</p>
                <p className="text-text-secondary">{editing.notes}</p>
              </div>
            )}
            <Select label="Status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input
              label="Internal notes"
              value={editForm.internalNotes}
              onChange={(e) => setEditForm({ ...editForm, internalNotes: e.target.value })}
              placeholder="Notes for the team — not visible to the lead"
            />
            <Button className="w-full" loading={saving} onClick={saveEdit}>Save</Button>
          </div>
        )}
      </Modal>

      <Modal open={!!converting} onClose={() => setConverting(null)} title={`Invite ${converting?.name || ''} as Partner`}>
        {converting && (
          <div className="space-y-3">
            <div className="bg-bg-panel border border-border rounded-xl p-3 text-xs space-y-1">
              <p className="text-text-secondary">{converting.name} · {converting.phone}</p>
              <p className="text-text-muted">Interested in: {converting.area}</p>
            </div>

            <Input
              label="Email"
              value={convertForm.email}
              onChange={(e) => setConvertForm({ ...convertForm, email: e.target.value })}
              placeholder={converting.email ? undefined : 'This lead did not give an email — enter one to send the invite'}
            />

            <Select label="Site to assign (optional)" value={convertForm.siteId} onChange={(e) => pickConvertSite(e.target.value)}>
              <option value="">No site — invite only</option>
              {availableSites.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.project?.name})</option>)}
            </Select>

            {selectedSite && (
              <div className="bg-bg-panel border border-border rounded-xl p-3 text-xs space-y-1">
                <p className="font-semibold text-text-primary">{selectedSite.name} — {selectedSite.project?.name}</p>
                <p className="text-text-secondary">Funding cost: <span className="font-semibold">{fmtUGX(selectedSite.fundingCost)}</span></p>
                <p className="text-text-secondary">Revenue share: <span className="font-semibold">{selectedSite.revenueSharePct}%</span>{selectedSite.shareTermMonths ? ` for ${selectedSite.shareTermMonths} months` : ''}</p>
              </div>
            )}

            <Input label="Contribution Amount (UGX)" type="number" value={convertForm.contributionAmount} onChange={(e) => setConvertForm({ ...convertForm, contributionAmount: e.target.value })} />
            <Button className="w-full" loading={saving} onClick={convert}>Send Invite</Button>
            <p className="text-text-muted text-xs text-center">
              This sends a private invite exactly like Partners → Invite Partner, pre-filled from this lead. The lead is marked "invited" once sent.
            </p>
          </div>
        )}
      </Modal>
    </Card>
  );
}

// ── Equipment ────────────────────────────────────────────────────────────────
const EMPTY_EQUIPMENT_FORM = { type: '', quantity: '1', unitPrice: '', serial: '', project: '', site: '', notes: '' };

function EquipmentForm({ form, setForm }) {
  const qty = Number(form.quantity) || 0;
  const price = Number(form.unitPrice) || 0;
  const total = qty * price;
  return (
    <div className="space-y-3">
      <Input label="Type / Name" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="e.g. MikroTik Router" />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Quantity" type="number" min="1" value={form.quantity}
          onChange={(e) => {
            const quantity = e.target.value;
            // A serial identifies one physical unit — clear it the moment
            // quantity moves off 1 so the two never end up in conflict.
            setForm({ ...form, quantity, serial: Number(quantity) > 1 ? '' : form.serial });
          }}
        />
        <Input label="Unit Price (UGX)" type="number" min="0" value={form.unitPrice} onChange={(e) => setForm({ ...form, unitPrice: e.target.value })} />
      </div>
      <div className="bg-bg-panel border border-border rounded-xl p-3 flex items-center justify-between">
        <span className="text-text-muted text-xs font-semibold">Total Price</span>
        <span className="text-text-primary text-sm font-bold">{fmtUGX(total)}</span>
      </div>
      <Input
        label={`Serial (optional${qty > 1 ? ' — only for a single unit' : ''})`}
        value={form.serial}
        disabled={qty > 1}
        onChange={(e) => setForm({ ...form, serial: e.target.value })}
        placeholder={qty > 1 ? 'Not available for quantity > 1' : 'e.g. SN-00123'}
      />
      <Input label="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
    </div>
  );
}

function EquipmentPanel() {
  const [equipment, setEquipment] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ project: '', site: '', status: '' });

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_EQUIPMENT_FORM);
  const [addTarget, setAddTarget] = useState({ project: '', site: '' });

  const [editing, setEditing] = useState(null); // the asset being edited
  const [editForm, setEditForm] = useState(EMPTY_EQUIPMENT_FORM);

  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, p, s] = await Promise.all([
        partnershipAPI.listEquipment(filters), partnershipAPI.listProjects(), partnershipAPI.listSites(),
      ]);
      setEquipment(eq.equipment); setSummary(eq.summary); setProjects(p.projects); setSites(s.sites);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setAddForm(EMPTY_EQUIPMENT_FORM); setAddTarget({ project: '', site: '' }); setShowAdd(true); };

  const add = async () => {
    if (!addForm.type.trim()) { toast.error('Type / name is required'); return; }
    setSaving(true);
    try {
      await partnershipAPI.addEquipment({
        type: addForm.type, quantity: Number(addForm.quantity) || 1, unitPrice: Number(addForm.unitPrice) || 0,
        serial: addForm.serial || null, project: addTarget.project || null, site: addTarget.site || null,
        notes: addForm.notes || null,
      });
      toast.success('Equipment added');
      setShowAdd(false);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to add equipment')); }
    finally { setSaving(false); }
  };

  const openEdit = (asset) => {
    setEditing(asset);
    setEditForm({
      type: asset.type, quantity: String(asset.quantity), unitPrice: String(asset.unitPrice),
      serial: asset.serial || '', project: '', site: '', notes: asset.notes || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await partnershipAPI.updateEquipment(editing._id, {
        type: editForm.type, quantity: Number(editForm.quantity) || 1, unitPrice: Number(editForm.unitPrice) || 0,
        serial: editForm.serial || null, notes: editForm.notes || null,
      });
      toast.success('Equipment updated');
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to update equipment')); }
    finally { setSaving(false); }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Remove "${asset.type}"? This cannot be undone.`)) return;
    try {
      await partnershipAPI.deleteEquipment(asset._id);
      toast.success('Equipment removed');
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to remove equipment')); }
  };

  const setStatus = async (asset, status) => {
    try {
      await partnershipAPI.updateEquipmentStatus(asset._id, status);
      toast.success('Status updated');
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to update status')); }
  };

  const assignToSite = async (asset, siteId) => {
    if (!siteId) return;
    try {
      await partnershipAPI.assignEquipment(asset._id, siteId);
      toast.success('Assigned to site');
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to assign to site')); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Cost" value={fmtUGX(summary?.totalCost)} />
        <StatCard label="Total Quantity" value={summary?.totalQuantity ?? 0} />
        <StatCard label="Deployed" value={summary?.deployedCount ?? 0} color="#22c55e" />
        <StatCard label="In Stock" value={summary?.inStockCount ?? 0} />
      </div>

      <Card>
        <SectionHeader title="Equipment" action={<Button size="sm" onClick={openAdd}>+ Add Equipment</Button>} />

        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filters.project} onChange={(e) => setFilters({ ...filters, project: e.target.value })} className="text-sm">
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>
          <Select value={filters.site} onChange={(e) => setFilters({ ...filters, site: e.target.value })} className="text-sm">
            <option value="">All Sites</option>
            {sites.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </Select>
          <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="text-sm">
            <option value="">All Statuses</option>
            {EQUIPMENT_STATUSES.map((st) => <option key={st} value={st}>{EQUIPMENT_STATUS_LABEL[st]}</option>)}
          </Select>
        </div>

        {loading ? <Spinner /> : equipment.length === 0 ? (
          <Empty title="No equipment tracked yet" />
        ) : (
          <Table headers={['Type', 'Qty', 'Unit Price', 'Total', 'Serial', 'Project', 'Site', 'Status', 'Actions']}>
            {equipment.map((e) => (
              <tr key={e._id}>
                <Td className="font-semibold text-text-primary">{e.type}</Td>
                <Td>{e.quantity}</Td>
                <Td className="text-xs">{fmtUGX(e.unitPrice)}</Td>
                <Td className="font-semibold">{fmtUGX(e.totalPrice)}</Td>
                <Td className="text-xs">{e.serial || '—'}</Td>
                <Td className="text-xs">{e.project?.name || '—'}</Td>
                <Td className="text-xs">{e.site?.name || '—'}</Td>
                <Td><Badge label={EQUIPMENT_STATUS_LABEL[e.status]} color={EQUIPMENT_STATUS_COLOR[e.status]} /></Td>
                <Td>
                  <div className="flex gap-1.5 flex-wrap items-center">
                    <button className="text-xs text-cyan hover:underline" onClick={() => openEdit(e)}>Edit</button>
                    <button className="text-xs text-red hover:underline" onClick={() => remove(e)}>Remove</button>
                    {!e.site && (
                      <Select value="" onChange={(ev) => assignToSite(e, ev.target.value)} className="text-xs">
                        <option value="">Assign to site…</option>
                        {sites.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                      </Select>
                    )}
                    <Select value="" onChange={(ev) => ev.target.value && setStatus(e, ev.target.value)} className="text-xs">
                      <option value="">Change status…</option>
                      {EQUIPMENT_STATUSES.filter((st) => st !== e.status).map((st) => (
                        <option key={st} value={st}>{EQUIPMENT_STATUS_LABEL[st]}</option>
                      ))}
                    </Select>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Equipment">
        <div className="space-y-3">
          <Select label="Project (optional)" value={addTarget.project} onChange={(e) => setAddTarget({ ...addTarget, project: e.target.value, site: '' })}>
            <option value="">Unassigned</option>
            {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>
          <Select label="Site (optional)" value={addTarget.site} onChange={(e) => setAddTarget({ ...addTarget, site: e.target.value })}>
            <option value="">Unassigned</option>
            {sites
              .filter((s) => !addTarget.project || s.project?._id === addTarget.project)
              .map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </Select>
          <EquipmentForm form={addForm} setForm={setAddForm} />
          <Button className="w-full" loading={saving} onClick={add}>Add Equipment</Button>
        </div>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit — ${editing?.type || ''}`}>
        <div className="space-y-3">
          <EquipmentForm form={editForm} setForm={setEditForm} />
          <Button className="w-full" loading={saving} onClick={saveEdit}>Save Changes</Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Earnings — site cost tracking + net-earnings ("Model B") ──────────────────
function EarningsPanel() {
  const [period, setPeriod] = useState(currentPeriod());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [costFor, setCostFor] = useState(null); // site being given a new cost
  const [costForm, setCostForm] = useState({ category: 'internet_subscription', amount: '', date: '', description: '', recurring: false });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null); // siteId currently syncing

  const load = useCallback(async () => {
    setLoading(true);
    try { setSummary(await partnershipAPI.getAdminEarningsSummary(period)); }
    catch (err) { toast.error(errMsg(err, 'Failed to load earnings summary')); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openCostForm = (site) => {
    setCostFor(site);
    setCostForm({ category: 'internet_subscription', amount: '', date: new Date().toISOString().slice(0, 10), description: '', recurring: false });
  };

  const submitCost = async () => {
    const amount = Number(costForm.amount);
    if (!amount || amount <= 0) { toast.error('Amount must be a positive number'); return; }
    setSaving(true);
    try {
      await partnershipAPI.recordSiteCost(costFor.siteId, { ...costForm, amount, period });
      toast.success('Cost recorded — the partner has been notified');
      setCostFor(null);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to record cost')); }
    finally { setSaving(false); }
  };

  const sync = async (site) => {
    setSyncing(site.siteId);
    try {
      await partnershipAPI.syncSiteNetEarnings(site.siteId, period);
      toast.success(`Net earnings for ${period} recorded as a PartnerEarning snapshot`);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to sync earnings')); }
    finally { setSyncing(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-xs text-text-muted font-semibold">Month</label>
        <input
          type="month"
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-text-primary"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
      </div>

      {loading || !summary ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard label="Total Revenue" value={fmtUGX(summary.totals.totalRevenueUGX)} />
            <StatCard label="Total Costs" value={fmtUGX(summary.totals.totalCostsUGX)} />
            <StatCard label="Net Profit" value={fmtUGX(summary.totals.totalNetProfitUGX)} />
            <StatCard label="Partner Share (Total)" value={fmtUGX(summary.totals.totalPartnerShareUGX)} color="#22c55e" />
            <StatCard label="DWI Share (Total)" value={fmtUGX(summary.totals.totalDwiShareUGX)} color="#06b6d4" />
          </div>

          <Card>
            <SectionHeader title={`Per-Site Breakdown — ${period}`} />
            {summary.sites.length === 0 ? (
              <Empty title="No partnered sites yet" description="Assign a partner to a site to start tracking net earnings." />
            ) : (
              <Table headers={['Site', 'Revenue (Sub / Hotspot)', 'Costs', 'Net Profit', 'Partner Share', 'DWI Share', 'Actions']}>
                {summary.sites.map((s) => (
                  <Fragment key={s.siteId}>
                    <tr>
                      <Td className="font-semibold text-text-primary">{s.siteName}</Td>
                      <Td className="text-xs">{fmtUGX(s.revenue.totalUGX)} <span className="text-text-muted">({fmtUGX(s.revenue.subscriberUGX)} / {fmtUGX(s.revenue.hotspotUGX)})</span></Td>
                      <Td>{fmtUGX(s.operatingCostsUGX)}</Td>
                      <Td className={s.netProfitUGX < 0 ? 'text-red font-semibold' : 'font-semibold'}>{fmtUGX(s.netProfitUGX)}</Td>
                      <Td className="font-semibold text-green">{fmtUGX(s.partnerShareUGX)}</Td>
                      <Td className="font-semibold text-cyan">{fmtUGX(s.dwiShareUGX)}</Td>
                      <Td>
                        <div className="flex gap-1.5 flex-wrap">
                          <button className="text-xs text-cyan hover:underline" onClick={() => openCostForm(s)}>+ Cost</button>
                          <button className="text-xs text-purple hover:underline" onClick={() => sync(s)} disabled={syncing === s.siteId}>
                            {syncing === s.siteId ? 'Syncing…' : 'Sync'}
                          </button>
                          <button className="text-xs text-text-muted hover:underline" onClick={() => setExpanded(expanded === s.siteId ? null : s.siteId)}>
                            {expanded === s.siteId ? 'Hide costs' : `Costs (${s.costs.length})`}
                          </button>
                        </div>
                      </Td>
                    </tr>
                    {expanded === s.siteId && (
                      <tr>
                        <td colSpan={7} className="bg-bg-panel/50 py-3 pr-4 text-sm text-text-secondary">
                          {s.costs.length === 0 ? (
                            <p className="text-xs text-text-muted py-1">No costs recorded for this site in {period}.</p>
                          ) : (
                            <ul className="space-y-1.5 py-1">
                              {s.costs.map((c) => (
                                <li key={c._id} className="text-xs flex items-center justify-between gap-2">
                                  <span>
                                    <Badge label={SITE_COST_CATEGORY_LABEL[c.category]} color="amber" />
                                    <span className="ml-2 text-text-secondary">{c.description || '—'}</span>
                                    {c.recurring && <span className="ml-2 text-text-muted">(recurring)</span>}
                                  </span>
                                  <span className="flex items-center gap-3">
                                    <span className="text-text-muted">{new Date(c.date).toLocaleDateString()}</span>
                                    <span className="font-semibold text-text-primary">{fmtUGX(c.amount)}</span>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </Table>
            )}
          </Card>
        </>
      )}

      <Modal open={!!costFor} onClose={() => setCostFor(null)} title={`Record Cost — ${costFor?.siteName || ''}`}>
        <div className="space-y-3">
          <p className="text-xs text-text-muted">Applies to {period}. Takes effect immediately — no partner approval required (consent is at contract signing). The partner is notified in-app.</p>
          <Select label="Category" value={costForm.category} onChange={(e) => setCostForm({ ...costForm, category: e.target.value })}>
            {SITE_COST_CATEGORIES.map((c) => <option key={c} value={c}>{SITE_COST_CATEGORY_LABEL[c]}</option>)}
          </Select>
          <Input label="Amount (UGX)" type="number" value={costForm.amount} onChange={(e) => setCostForm({ ...costForm, amount: e.target.value })} />
          <Input label="Date" type="date" value={costForm.date} onChange={(e) => setCostForm({ ...costForm, date: e.target.value })} />
          <Input label="Description (optional)" value={costForm.description} onChange={(e) => setCostForm({ ...costForm, description: e.target.value })} />
          <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
            <input type="checkbox" checked={costForm.recurring} onChange={(e) => setCostForm({ ...costForm, recurring: e.target.checked })} />
            Recurring cost
          </label>
          <Button className="w-full" loading={saving} onClick={submitCost}>Record Cost</Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Geography — national hierarchy for organizing sites at scale ──────────────
function GeographyPanel() {
  const [level, setLevel] = useState('country');
  const [units, setUnits] = useState([]);
  const [allByLevel, setAllByLevel] = useState({}); // level -> units[], for parent pickers
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', level: 'country', parentId: '', code: '' });
  const [saving, setSaving] = useState(false);
  const [counts, setCounts] = useState(null);
  const [countsLevel, setCountsLevel] = useState('country');
  const [countsLoading, setCountsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnershipAPI.listGeoUnits({ level });
      setUnits(res.units);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [level]);

  useEffect(() => { load(); }, [load]);

  // Parent-picker options — fetched lazily per level, cached.
  const loadParentOptions = useCallback(async (parentLevel) => {
    if (!parentLevel || allByLevel[parentLevel]) return;
    try {
      const res = await partnershipAPI.listGeoUnits({ level: parentLevel });
      setAllByLevel((m) => ({ ...m, [parentLevel]: res.units }));
    } catch (err) { toast.error(errMsg(err)); }
  }, [allByLevel]);

  const openCreate = () => {
    setForm({ name: '', level: 'country', parentId: '', code: '' });
    setShowCreate(true);
  };

  const changeFormLevel = (newLevel) => {
    setForm({ ...form, level: newLevel, parentId: '' });
    const idx = GEO_LEVELS.indexOf(newLevel);
    if (idx > 0) loadParentOptions(GEO_LEVELS[idx - 1]);
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.level !== 'country' && !form.parentId) { toast.error(`A ${GEO_LEVEL_LABEL[form.level]} requires a parent`); return; }
    setSaving(true);
    try {
      await partnershipAPI.createGeoUnit({
        name: form.name, level: form.level, parentId: form.parentId || undefined, code: form.code || undefined,
      });
      toast.success(`${GEO_LEVEL_LABEL[form.level]} created`);
      setShowCreate(false);
      setAllByLevel({}); // invalidate parent-picker caches — the new unit may itself become a parent
      if (form.level === level) load();
    } catch (err) { toast.error(errMsg(err, 'Failed to create geo unit')); }
    finally { setSaving(false); }
  };

  const loadCounts = useCallback(async (lvl) => {
    setCountsLoading(true);
    try { setCounts(await partnershipAPI.getGeoCounts(lvl)); }
    catch (err) { toast.error(errMsg(err, 'Failed to load counts')); }
    finally { setCountsLoading(false); }
  }, []);

  useEffect(() => { loadCounts(countsLevel); }, [countsLevel, loadCounts]);

  const parentLevel = GEO_LEVELS[GEO_LEVELS.indexOf(form.level) - 1];
  const parentOptions = allByLevel[parentLevel] || [];

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader title="Geographic Hierarchy" action={<Button size="sm" onClick={openCreate}>+ New Unit</Button>} />
        <p className="text-xs text-text-muted mb-3">
          Country → Region → District → Division → Parish → Village → Community → Site. Data-model foundation for
          national scaling — no maps, just structure so sites can be grouped and counted at any level.
        </p>
        <div className="flex gap-2 flex-wrap mb-4">
          {GEO_LEVELS.map((l) => (
            <button
              key={l}
              onClick={() => setLevel(l)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                level === l ? 'bg-cyan/15 text-cyan border border-cyan/30' : 'bg-bg-panel text-text-muted border border-border hover:text-text-primary'
              }`}
            >
              {GEO_LEVEL_LABEL[l]}
            </button>
          ))}
        </div>

        {loading ? <Spinner /> : units.length === 0 ? (
          <Empty title={`No ${GEO_LEVEL_LABEL[level].toLowerCase()} units yet`} description="Create one to start building out the hierarchy." />
        ) : (
          <Table headers={['Name', 'Code', 'Created']}>
            {units.map((u) => (
              <tr key={u._id}>
                <Td className="font-semibold text-text-primary">{u.name}</Td>
                <Td className="text-xs">{u.code || '—'}</Td>
                <Td className="text-text-muted text-xs">{new Date(u.createdAt).toLocaleDateString()}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <SectionHeader title="Sites by Level" />
        <div className="flex gap-2 flex-wrap mb-4">
          {GEO_LEVELS.map((l) => (
            <button
              key={l}
              onClick={() => setCountsLevel(l)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                countsLevel === l ? 'bg-purple/15 text-purple border border-purple/30' : 'bg-bg-panel text-text-muted border border-border hover:text-text-primary'
              }`}
            >
              {GEO_LEVEL_LABEL[l]}
            </button>
          ))}
        </div>
        {countsLoading ? <Spinner /> : !counts?.counts?.length ? (
          <Empty title="No sites counted at this level yet" description="Assign sites to a geo unit at or below this level from the Sites tab." />
        ) : (
          <Table headers={[GEO_LEVEL_LABEL[countsLevel], 'Sites']}>
            {counts.counts.map((c) => (
              <tr key={c.geoUnitId}>
                <Td className="font-semibold text-text-primary">{c.name}</Td>
                <Td>{c.siteCount}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Geo Unit">
        <div className="space-y-3">
          <Select label="Level" value={form.level} onChange={(e) => changeFormLevel(e.target.value)}>
            {GEO_LEVELS.map((l) => <option key={l} value={l}>{GEO_LEVEL_LABEL[l]}</option>)}
          </Select>
          {form.level !== 'country' && (
            <Select label={`Parent (${GEO_LEVEL_LABEL[parentLevel]})`} value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
              <option value="">Select {GEO_LEVEL_LABEL[parentLevel].toLowerCase()}…</option>
              {parentOptions.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </Select>
          )}
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Code (optional)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Button className="w-full" loading={saving} onClick={create}>Create</Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Map — Leaflet + OpenStreetMap, CDN-loaded (no Google Maps, no API key,
// no billing, no new npm dependency). Admin-only, read-only. ─────────────────
const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
let leafletLoadPromise = null;

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.getElementById('leaflet-css-cdn')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css-cdn';
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS_URL;
      document.head.appendChild(link);
    }

    const existing = document.getElementById('leaflet-js-cdn');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', () => reject(new Error('Failed to load Leaflet from CDN')));
      return;
    }

    const script = document.createElement('script');
    script.id = 'leaflet-js-cdn';
    script.src = LEAFLET_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load Leaflet from CDN'));
    document.body.appendChild(script);
  });

  return leafletLoadPromise;
}

function MapPanel() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await partnershipAPI.listSites();
        setSites(res.sites || []);
      } catch (err) { toast.error(errMsg(err, 'Failed to load sites')); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    let cancelled = false;

    loadLeaflet().then((L) => {
      if (cancelled || !containerRef.current) return;

      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current).setView(
          [mapUtil.UGANDA_CENTER.lat, mapUtil.UGANDA_CENTER.lng], mapUtil.DEFAULT_ZOOM
        );
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(mapRef.current);
      }
      const map = mapRef.current;

      // Clear previous markers/circles before redrawing (site list can change).
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) return;
        map.removeLayer(layer);
      });

      const mappable = mapUtil.sitesWithGps(sites);
      for (const site of mappable) {
        const color = mapUtil.statusColor(site.status);
        L.circleMarker([site.gpsLat, site.gpsLng], {
          radius: 8, color, weight: 2, fillColor: color, fillOpacity: 0.85,
        }).addTo(map).bindPopup(mapUtil.popupHtml(site));

        if (mapUtil.hasCoverage(site)) {
          L.circle([site.gpsLat, site.gpsLng], {
            radius: site.coverageRadius, color, weight: 1, fillColor: color, fillOpacity: 0.12,
          }).addTo(map);
        }
      }
    }).catch((err) => {
      if (!cancelled) setMapError(err.message);
    });

    return () => { cancelled = true; };
  }, [loading, sites]);

  useEffect(() => () => { mapRef.current?.remove(); mapRef.current = null; }, []);

  const mappableCount = mapUtil.sitesWithGps(sites).length;

  return (
    <Card>
      <SectionHeader title="Sites Map" />
      <p className="text-xs text-text-muted mb-3">
        OpenStreetMap — free, no API key or billing. {mappableCount} of {sites.length} site{sites.length === 1 ? '' : 's'} have GPS
        coordinates and appear below. Set a site's GPS from the Sites tab to place it on the map.
      </p>

      <div className="flex gap-3 flex-wrap mb-3 text-xs">
        {Object.entries(mapUtil.STATUS_COLOR).map(([status, color]) => (
          <div key={status} className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-text-secondary">{SITE_STATUS_LABEL[status]}</span>
          </div>
        ))}
      </div>

      {loading ? <Spinner /> : mapError ? (
        <Empty title="Map failed to load" description={mapError} />
      ) : (
        <div ref={containerRef} style={{ height: '600px', width: '100%', borderRadius: '12px' }} />
      )}
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PARTNER PORTAL — read-only, own data only
// ═════════════════════════════════════════════════════════════════════════════

export default function PartnerPortal() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [sites, setSites] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [progress, setProgress] = useState({});
  const [performance, setPerformance] = useState({});
  const [equipment, setEquipment] = useState({});
  const [network, setNetwork] = useState({});
  const [networkError, setNetworkError] = useState({});
  const [upgradeHistory, setUpgradeHistory] = useState({});
  const [earningsPeriod, setEarningsPeriod] = useState({}); // siteId -> "YYYY-MM"
  const [monthlyEarnings, setMonthlyEarnings] = useState({}); // "siteId:period" -> breakdown
  const [monthlyEarningsLoading, setMonthlyEarningsLoading] = useState(false);
  const [movingEarnings, setMovingEarnings] = useState(false);

  // Move all tracked earnings into the partner's DWIPay wallet. The amount is
  // decided entirely server-side from the partner's tracked PartnerEarning
  // records — this just triggers it and re-reads the earnings afterwards.
  const moveEarningsToWallet = async (withdrawableUGX) => {
    if (!window.confirm(`Move ${fmtUGX(withdrawableUGX)} into your wallet? You can then cash out from Wallet → Withdraw.`)) return;
    setMovingEarnings(true);
    try {
      const res = await partnershipAPI.portalMoveEarningsToWallet();
      toast.success(`${fmtUGX(res.movedUGX)} moved to your wallet — new balance ${fmtUGX(res.walletBalanceUGX)}.`);
      setEarnings(await partnershipAPI.portalEarnings());
    } catch (err) {
      toast.error(errMsg(err, 'Could not move your earnings to your wallet'));
    } finally {
      setMovingEarnings(false);
    }
  };

  // Live router telemetry (connected clients, uptime) for one site. Called
  // on initial load for every site (not gated behind expanding the site
  // row) so a partner sees their live user count immediately, and reused
  // by the "Retry" affordance below if the fetch fails.
  const fetchNetworkForSite = async (siteId) => {
    setNetworkError((ne) => ({ ...ne, [siteId]: false }));
    try {
      const res = await networkAPI.portalSiteRouter(siteId);
      setNetwork((nw) => ({ ...nw, [siteId]: res.router }));
    } catch (err) {
      // Feature may be flag-gated off, or the site has no router yet —
      // surface a small inline retry affordance rather than staying blank.
      setNetwork((nw) => ({ ...nw, [siteId]: undefined }));
      setNetworkError((ne) => ({ ...ne, [siteId]: true }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [meRes, sitesRes, earnRes, finRes] = await Promise.all([
          partnershipAPI.portalMe(), partnershipAPI.portalSites(), partnershipAPI.portalEarnings(), partnershipAPI.portalFinancialSummary(),
        ]);
        setMe(meRes.partner); setSites(sitesRes.sites); setEarnings(earnRes); setFinancial(finRes);
        sitesRes.sites.forEach((s) => fetchNetworkForSite(s._id));
      } catch (err) {
        toast.error(errMsg(err, 'Failed to load your partnership dashboard'));
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleSite = async (site) => {
    if (expanded === site._id) { setExpanded(null); return; }
    setExpanded(site._id);
    if (!progress[site._id]) {
      try {
        const res = await partnershipAPI.portalSiteProgress(site._id);
        setProgress((p) => ({ ...p, [site._id]: res.updates }));
      } catch (err) { toast.error(errMsg(err)); }
    }
    if (!performance[site._id]) {
      try {
        const res = await partnershipAPI.portalSitePerformance(site._id);
        setPerformance((p) => ({ ...p, [site._id]: res.performance }));
      } catch (err) { toast.error(errMsg(err)); }
    }
    if (!equipment[site._id]) {
      try {
        const res = await partnershipAPI.portalSiteEquipment(site._id);
        setEquipment((eq) => ({ ...eq, [site._id]: res.equipment }));
      } catch (err) { toast.error(errMsg(err)); }
    }
    if (!upgradeHistory[site._id]) {
      try {
        const res = await partnershipAPI.portalSiteUpgrades(site._id);
        setUpgradeHistory((h) => ({ ...h, [site._id]: res.upgrades }));
      } catch (err) { toast.error(errMsg(err)); }
    }
    if (!earningsPeriod[site._id]) {
      const p = currentPeriod();
      setEarningsPeriod((ep) => ({ ...ep, [site._id]: p }));
      loadMonthlyEarnings(site._id, p);
    }
  };

  const loadMonthlyEarnings = async (siteId, period) => {
    const key = `${siteId}:${period}`;
    if (monthlyEarnings[key]) return;
    setMonthlyEarningsLoading(true);
    try {
      const res = await partnershipAPI.portalSiteMonthlyEarnings(siteId, period);
      setMonthlyEarnings((m) => ({ ...m, [key]: res.breakdown }));
    } catch (err) { toast.error(errMsg(err, 'Failed to load earnings breakdown')); }
    finally { setMonthlyEarningsLoading(false); }
  };

  const changeEarningsPeriod = (siteId, period) => {
    setEarningsPeriod((ep) => ({ ...ep, [siteId]: period }));
    loadMonthlyEarnings(siteId, period);
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  if (user?.role !== 'partner') {
    return <Empty title="Not available" description="This page is only available to partnership network partners." />;
  }

  return (
    <div className="space-y-6 animate-slide-up max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-black text-text-primary tracking-tight">Welcome, {me?.name}</h1>
        <p className="text-text-secondary text-sm mt-1">Your projects, sites, and earnings — updated by DWI as your venture grows.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Your Sites" value={sites.length} />
        <StatCard label="Total Contributed" value={fmtUGX(earnings?.sitesSummary?.totalFundingCost)} />
        <StatCard label="Total Revenue Generated" value={fmtUGX(earnings?.summary?.totalRevenueUGX)} />
        <StatCard label="Your Share Earned" value={fmtUGX(earnings?.summary?.totalEarnedUGX)} color="#22c55e" />
      </div>

      {earnings?.payoutEnabled && (() => {
        const rows = earnings?.earnings || [];
        const tracked = rows.filter((e) => (e.status || 'tracked') === 'tracked');
        const credited = rows.filter((e) => e.status === 'credited');
        const withdrawable = earnings?.summary?.withdrawableUGX
          ?? tracked.reduce((s, e) => s + (e.earnedAmountUGX || 0), 0);
        return (
          <Card>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-text-primary">Withdrawable Earnings</h2>
                <p className="text-text-secondary text-xs mt-1 max-w-md">
                  Your tracked site earnings. Move them into your DWIPay wallet, then cash out
                  via <span className="font-semibold text-text-primary">Wallet → Withdraw</span>.
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-2xl font-black text-green">{fmtUGX(withdrawable)}</p>
                <Button
                  size="sm"
                  variant="success"
                  className="mt-1"
                  loading={movingEarnings}
                  disabled={movingEarnings || withdrawable <= 0}
                  onClick={() => moveEarningsToWallet(withdrawable)}
                >
                  Move to My Wallet
                </Button>
              </div>
            </div>

            {tracked.length > 0 ? (
              <div className="mt-4 overflow-x-auto">
                <Table headers={['Site', 'Period', 'Revenue', 'Costs', 'Share %', 'Earned']}>
                  {tracked.map((e) => (
                    <tr key={e._id}>
                      <Td className="text-xs font-semibold text-text-primary">{e.site?.name || '—'}</Td>
                      <Td className="text-xs">{fmtEarningPeriod(e)}</Td>
                      <Td className="text-xs">{fmtUGX(e.grossRevenueUGX)}</Td>
                      <Td className="text-xs">{e.operatingCostsUGX != null ? fmtUGX(e.operatingCostsUGX) : '—'}</Td>
                      <Td className="text-xs">{e.revenueSharePct}%</Td>
                      <Td className="text-xs font-semibold text-green">{fmtUGX(e.earnedAmountUGX)}</Td>
                    </tr>
                  ))}
                </Table>
              </div>
            ) : (
              <p className="text-text-muted text-xs mt-4">
                Nothing waiting to be moved. New earnings appear here as DWI records each month's revenue share.
              </p>
            )}

            {credited.length > 0 && (
              <div className="mt-5">
                <p className="text-text-primary text-xs font-semibold mb-2">Credited History</p>
                <div className="overflow-x-auto">
                  <Table headers={['Site', 'Period', 'Earned', 'Moved to wallet']}>
                    {credited.map((e) => (
                      <tr key={e._id}>
                        <Td className="text-xs">{e.site?.name || '—'}</Td>
                        <Td className="text-xs">{fmtEarningPeriod(e)}</Td>
                        <Td className="text-xs font-semibold">{fmtUGX(e.earnedAmountUGX)}</Td>
                        <Td className="text-xs text-text-muted">{e.creditedAt ? new Date(e.creditedAt).toLocaleDateString() : '—'}</Td>
                      </tr>
                    ))}
                  </Table>
                </div>
              </div>
            )}
          </Card>
        );
      })()}

      <div>
        <h2 className="text-lg font-bold text-text-primary mb-3">Your Sites</h2>
        {sites.length === 0 ? (
          <Card><Empty title="No sites yet" description="DWI will assign you to a site once your venture is set up." /></Card>
        ) : (
          <div className="space-y-3">
            {sites.map((s) => (
              <Card key={s._id} className="cursor-pointer" onClick={() => toggleSite(s)}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-text-primary">{s.name}</p>
                    <p className="text-text-muted text-xs">{s.project?.name}{s.location ? ' · ' + s.location : ''}</p>
                  </div>
                  <Badge label={SITE_STATUS_LABEL[s.status]} color={SITE_STATUS_COLOR[s.status]} />
                </div>

                {/* Live Users (now) — real router telemetry, fetched on page
                    load, always visible (not gated behind expanding this
                    card). Deliberately labeled distinctly from "Active
                    Subscriptions" below, which is a subscription-validity
                    count, not a live connection count. */}
                <div className="mt-3 flex items-center justify-between gap-2 bg-bg-panel border border-border rounded-lg px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                  {networkError[s._id] ? (
                    <>
                      <span className="text-text-muted">⚠ Live data unavailable — retrying…</span>
                      <button onClick={() => fetchNetworkForSite(s._id)} className="text-cyan font-semibold hover:underline">
                        Retry
                      </button>
                    </>
                  ) : !network[s._id] ? (
                    <span className="text-text-muted flex items-center gap-2"><Spinner size="sm" /> Loading live users…</span>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: network[s._id].monitoringStatus === 'online' ? '#22c55e' : network[s._id].monitoringStatus === 'offline' ? '#ef4444' : '#94a3b8' }}
                        />
                        <span className="text-text-primary font-black text-base">{network[s._id].connectedClients ?? 0}</span>
                        <span className="text-text-secondary font-semibold">Live Users (now)</span>
                      </div>
                      <div className="flex items-center gap-2 text-text-muted">
                        {network[s._id].uptimePercent != null && <span>{network[s._id].uptimePercent}% uptime (30d)</span>}
                        <Badge
                          label={MONITORING_LABEL[network[s._id].monitoringStatus] || 'Pending'}
                          color={MONITORING_COLOR[network[s._id].monitoringStatus] || 'text-muted'}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-3 text-xs text-text-secondary">
                  Your share: <span className="font-semibold text-text-primary">{s.revenueSharePct}%</span>
                  {s.shareTermMonths ? ` for ${s.shareTermMonths} months` : ''}
                  {s.goLiveDate ? <> · Live since {new Date(s.goLiveDate).toLocaleDateString()}</> : null}
                </div>

                {expanded === s._id && (
                  <div className="mt-4 pt-4 border-t border-border" onClick={(e) => e.stopPropagation()}>
                    <p className="text-text-primary text-xs font-semibold mb-2">Live Performance</p>
                    {!performance[s._id] ? (
                      <Spinner size="sm" />
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        <StatCard label="Total Subscribers" value={performance[s._id].customersConnected} />
                        <StatCard label="Active Subscriptions" value={performance[s._id].customersActive} color="#22c55e" />
                        <StatCard label="Revenue Generated" value={fmtUGX(performance[s._id].totalRevenueUGX)} />
                        <StatCard label="Your Earnings" value={fmtUGX(performance[s._id].totalEarnedUGX)} color="#22c55e" />
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-2">
                      <p className="text-text-primary text-xs font-semibold">Monthly Earnings Breakdown</p>
                      <input
                        type="month"
                        className="bg-bg-panel border border-border rounded-lg px-2 py-1 text-xs text-text-primary"
                        value={earningsPeriod[s._id] || currentPeriod()}
                        onChange={(e) => changeEarningsPeriod(s._id, e.target.value)}
                      />
                    </div>
                    {(() => {
                      const key = `${s._id}:${earningsPeriod[s._id] || currentPeriod()}`;
                      const b = monthlyEarnings[key];
                      if (!b) return monthlyEarningsLoading ? <div className="mb-4"><Spinner size="sm" /></div> : null;
                      return (
                        <div className="mb-4 space-y-2">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <StatCard label="Subscriber Revenue" value={fmtUGX(b.revenue.subscriberUGX)} />
                            <StatCard label="Hotspot Revenue" value={fmtUGX(b.revenue.hotspotUGX)} />
                            <StatCard label="Total Revenue" value={fmtUGX(b.revenue.totalUGX)} />
                            <StatCard label="Net Profit" value={fmtUGX(b.netProfitUGX)} color={b.netProfitUGX < 0 ? '#ef4444' : undefined} />
                          </div>
                          <div className="bg-bg-panel border border-border rounded-lg p-3 text-xs">
                            <p className="text-text-muted mb-1.5">Operating costs deducted ({b.costs.length}):</p>
                            {b.costs.length === 0 ? (
                              <p className="text-text-muted italic">None recorded for this month.</p>
                            ) : (
                              <ul className="space-y-1">
                                {b.costs.map((c) => (
                                  <li key={c._id} className="flex items-center justify-between gap-2">
                                    <span className="text-text-secondary">
                                      <Badge label={SITE_COST_CATEGORY_LABEL[c.category]} color="amber" />
                                      {c.description && <span className="ml-2">{c.description}</span>}
                                    </span>
                                    <span className="text-text-muted">{new Date(c.date).toLocaleDateString()} · <span className="text-text-primary font-semibold">{fmtUGX(c.amount)}</span></span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="border-t border-border mt-2 pt-2 flex items-center justify-between font-semibold">
                              <span className="text-text-secondary">Total costs deducted</span>
                              <span className="text-text-primary">{fmtUGX(b.operatingCostsUGX)}</span>
                            </div>
                          </div>
                          <div className="bg-green/10 border border-green/25 rounded-lg p-3 text-xs flex items-center justify-between">
                            <span className="text-text-secondary">Revenue ({fmtUGX(b.revenue.totalUGX)}) − Costs ({fmtUGX(b.operatingCostsUGX)}) = Net Profit ({fmtUGX(b.netProfitUGX)}) × {b.revenueSharePct}%</span>
                            <span className="text-green font-bold text-sm">{fmtUGX(b.partnerShareUGX)}</span>
                          </div>
                        </div>
                      );
                    })()}

                    <p className="text-text-primary text-xs font-semibold mb-2">Equipment</p>
                    {!equipment[s._id] ? (
                      <Spinner size="sm" />
                    ) : equipment[s._id].length === 0 ? (
                      <p className="text-text-muted text-xs mb-4">No equipment recorded at this site yet.</p>
                    ) : (
                      <ul className="space-y-2 mb-4">
                        {equipment[s._id].map((e) => (
                          <li key={e._id} className="flex items-center justify-between gap-2 text-xs bg-bg-panel border border-border rounded-lg px-3 py-2">
                            <div>
                              <span className="text-text-primary font-semibold">{e.type}</span>
                              <span className="text-text-muted ml-2">×{e.quantity}</span>
                              {e.serial && <span className="text-text-muted ml-2 font-mono">SN: {e.serial}</span>}
                            </div>
                            <Badge label={EQUIPMENT_STATUS_LABEL[e.status]} color={EQUIPMENT_STATUS_COLOR[e.status]} />
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="text-text-primary text-xs font-semibold mb-2">Capacity</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                      <StatCard label="Current Capacity" value={s.currentCapacity ?? 0} />
                      <StatCard label="Active Customers" value={s.activeCustomers ?? 0} />
                      <StatCard label="Available" value={s.availableCapacity ?? 0} color="#22c55e" />
                      <StatCard label="Utilization" value={`${s.utilizationPct ?? 0}%`} color={s.utilizationPct >= 80 ? '#ef4444' : s.utilizationPct >= 60 ? '#f59e0b' : '#22c55e'} />
                    </div>

                    <p className="text-text-primary text-xs font-semibold mb-2">Upgrade History</p>
                    {!upgradeHistory[s._id] ? (
                      <Spinner size="sm" />
                    ) : upgradeHistory[s._id].length === 0 ? (
                      <p className="text-text-muted text-xs mb-4">No upgrades recorded yet — still on the original deployment.</p>
                    ) : (
                      <ul className="space-y-2 mb-4">
                        {upgradeHistory[s._id].map((h) => (
                          <li key={h._id} className="text-xs bg-bg-panel border border-border rounded-lg px-3 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-text-primary font-semibold">{h.description}</span>
                              <span className="text-text-muted">{new Date(h.date).toLocaleDateString()}</span>
                            </div>
                            <div className="text-text-muted mt-0.5">Capacity {h.previousCapacity} → {h.newCapacity}</div>
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="text-text-primary text-xs font-semibold mb-2">Progress Timeline</p>
                    {!progress[s._id] ? (
                      <Spinner size="sm" />
                    ) : progress[s._id].length === 0 ? (
                      <p className="text-text-muted text-xs">No updates posted yet.</p>
                    ) : (
                      <ul className="space-y-2">
                        {progress[s._id].map((u) => (
                          <li key={u._id} className="flex gap-2 text-xs items-start">
                            <span className="text-cyan flex-shrink-0 mt-0.5">&#x2022;</span>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-text-primary font-semibold">{u.title || u.message}</span>
                                <Badge label={UPDATE_TYPE_LABEL[u.updateType] || 'General'} color={UPDATE_TYPE_COLOR[u.updateType] || 'text-muted'} />
                              </div>
                              {u.title && <p className="text-text-secondary mt-0.5">{u.message}</p>}
                              <span className="text-text-muted">{new Date(u.createdAt).toLocaleDateString()}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {financial?.sites?.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-text-primary mb-3">Financial Summary</h2>
          <Card>
            <Table headers={['Site', 'Contribution', 'Share %', 'Term', 'Earned to Date', 'Payout Status']}>
              {financial.sites.map((s) => (
                <tr key={s.siteId}>
                  <Td className="font-semibold text-text-primary">{s.siteName}</Td>
                  <Td>{fmtUGX(s.contributionUGX)}</Td>
                  <Td>{s.revenueSharePct}%</Td>
                  <Td className="text-xs">{s.shareTermMonths ? `${s.shareTermMonths} months` : '—'}</Td>
                  <Td className="font-semibold text-green">{fmtUGX(s.earnedToDateUGX)}</Td>
                  <Td><Badge label="Not yet available" color="text-muted" /></Td>
                </tr>
              ))}
            </Table>
            <div className="flex gap-6 mt-4 pt-4 border-t border-border text-xs">
              <div><span className="text-text-muted">Total Contributed: </span><span className="font-semibold text-text-primary">{fmtUGX(financial.combined.totalContributedUGX)}</span></div>
              <div><span className="text-text-muted">Total Earned: </span><span className="font-semibold text-green">{fmtUGX(financial.combined.totalEarnedUGX)}</span></div>
            </div>
          </Card>
        </div>
      )}

      {earnings?.earnings?.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-text-primary mb-3">Earnings History</h2>
          <Card>
            <Table headers={['Site', 'Period', 'Revenue', 'Share', 'Your Earning']}>
              {earnings.earnings.map((e) => (
                <tr key={e._id}>
                  <Td>{e.site?.name}</Td>
                  <Td className="text-xs">{new Date(e.periodStart).toLocaleDateString()} – {new Date(e.periodEnd).toLocaleDateString()}</Td>
                  <Td>{fmtUGX(e.grossRevenueUGX)}</Td>
                  <Td>{e.revenueSharePct}%</Td>
                  <Td className="font-semibold text-green">{fmtUGX(e.earnedAmountUGX)}</Td>
                </tr>
              ))}
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
